import * as THREE from 'three';
import type { GameContext } from '../core/context';
import { Rng } from '../core/rng';
import type { System } from '../core/system';
import { onMood, type WeatherKind } from '../render/mood';
import { viewpointFocus } from '../render/viewpoints';

/**
 * Environmental reactions (owner: world-artist). Visual only, fully pooled — every buffer is
 * allocated once at init, per-frame work never allocates:
 *
 *  - scorch decals where vehicles explode (multiply-blended, fade over ~2 minutes)
 *  - rain ripples on the wet ground around the camera (drizzle/rain moods)
 *  - steam from street vents (through the shared `particles` service)
 *  - paper / leaf litter on the pavements that is blown up by passing cars
 */
const SCORCH_MAX = 24;
const RIPPLE_MAX = 220;
const LITTER_MAX = 160;
const GROUND_Y = 0.135;

function canvasTexture(size: number, paint: (g: CanvasRenderingContext2D, s: number) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  paint(g, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function createEnvFxSystem(): System {
  let ctx: GameContext;
  const rng = new Rng(9001);
  let weather: WeatherKind = 'drizzle';
  let unsubMood: (() => void) | null = null;
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const col = new THREE.Color();
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);

  // ---- scorch decals
  let scorch: THREE.InstancedMesh | null = null;
  const scorchAge = new Float32Array(SCORCH_MAX).fill(-1);
  const scorchLife = 150;
  let scorchNext = 0;

  // ---- ripples
  let ripples: THREE.InstancedMesh | null = null;
  const rx = new Float32Array(RIPPLE_MAX);
  const rz = new Float32Array(RIPPLE_MAX);
  const rAge = new Float32Array(RIPPLE_MAX).fill(-1);
  let rNext = 0;
  let rAcc = 0;

  // ---- litter
  let litter: THREE.InstancedMesh | null = null;
  const lp = new Float32Array(LITTER_MAX * 3);
  const lv = new Float32Array(LITTER_MAX * 3);
  const lrot = new Float32Array(LITTER_MAX * 3);
  const lspin = new Float32Array(LITTER_MAX * 3);
  const lAir = new Uint8Array(LITTER_MAX);
  const lDirty = new Uint8Array(LITTER_MAX);
  /**
   * Cars fast enough to gust litter this frame, within reach of the litter field: x, z, vx, vz, speed per
   * car. Gathered once per frame; the litter loop used to ask every car in the borough for its speed for
   * every piece of litter (160 × ~80 getter calls a frame, docs/PERF.md "Phone pass").
   */
  const GUST_MAX = 48;
  const gusts = new Float32Array(GUST_MAX * 5);
  let gustCount = 0;
  let pavement: Float32Array = new Float32Array(0);
  /** Pavement point indices bucketed by 20 m cell, for cheap 'near the camera' sampling. */
  const paveCells = new Map<number, number[]>();
  const CELL = 20;
  const cellKey = (cx: number, cz: number): number => (cx + 500) * 1000 + (cz + 500);
  let litterSeeded = false;

  // ---- steam vents
  let vents: Float32Array = new Float32Array(0);
  let ventAcc = 0;

  const focus = (): { x: number; z: number } => viewpointFocus() ?? ctx.services.tryGet('cameraRig')?.lookAt ?? { x: 0, z: 0 };

  function placeScorch(x: number, z: number, radius: number): void {
    if (!scorch) return;
    const i = scorchNext;
    scorchNext = (scorchNext + 1) % SCORCH_MAX;
    scorchAge[i] = 0;
    e.set(0, rng.range(0, Math.PI * 2), 0);
    q.setFromEuler(e);
    m.compose(v.set(x, GROUND_Y + 0.004 + i * 0.0004, z), q, s.set(radius * 2, 1, radius * 2 * rng.range(0.8, 1.2)));
    scorch.setMatrixAt(i, m);
    scorch.setColorAt(i, col.setRGB(1, 1, 1));
    scorch.instanceMatrix.needsUpdate = true;
  }

  function respawnLitter(i: number, f: { x: number; z: number }): void {
    // a random pavement point in a random cell near the camera (no allocation)
    let px = Number.NaN;
    let pz = 0;
    for (let k = 0; k < 8 && Number.isNaN(px); k++) {
      const cx = Math.floor((f.x + rng.range(-50, 50)) / CELL);
      const cz = Math.floor((f.z + 8 + rng.range(-36, 36)) / CELL);
      const list = paveCells.get(cellKey(cx, cz));
      if (!list || list.length === 0) continue;
      const j = list[Math.floor(rng.next() * list.length)]!;
      px = pavement[j * 2]!;
      pz = pavement[j * 2 + 1]!;
    }
    if (Number.isNaN(px)) {
      // nothing nearby yet: park it out of sight and try again later
      lp[i * 3] = 1e5;
      lp[i * 3 + 2] = 1e5;
      litter?.setMatrixAt(i, zero);
      return;
    }
    lp[i * 3] = px + rng.range(-1.5, 1.5);
    lp[i * 3 + 1] = GROUND_Y + 0.01;
    lp[i * 3 + 2] = pz + rng.range(-1.5, 1.5);
    lv[i * 3] = lv[i * 3 + 1] = lv[i * 3 + 2] = 0;
    lrot[i * 3] = -Math.PI / 2 + rng.range(-0.15, 0.15);
    lrot[i * 3 + 1] = rng.range(0, Math.PI * 2);
    lrot[i * 3 + 2] = 0;
    lAir[i] = 0;
    lDirty[i] = 1;
  }

  return {
    id: 'env-fx',
    order: 725,
    init(c) {
      ctx = c;
      unsubMood = onMood((md) => {
        weather = md.weather;
      });
      if (!c.render) return;

      // scorch
      const scorchTex = canvasTexture(128, (g, n) => {
        g.clearRect(0, 0, n, n);
        const r = new Rng(7);
        for (let k = 0; k < 40; k++) {
          const a = r.range(0, Math.PI * 2);
          const d = r.range(0, n * 0.28);
          const rad = r.range(n * 0.08, n * 0.22);
          const x = n / 2 + Math.cos(a) * d;
          const y = n / 2 + Math.sin(a) * d;
          const grad = g.createRadialGradient(x, y, 0, x, y, rad);
          grad.addColorStop(0, 'rgba(14,11,9,0.8)');
          grad.addColorStop(1, 'rgba(20,16,12,0)');
          g.fillStyle = grad;
          g.fillRect(0, 0, n, n);
        }
      });
      const scorchMat = new THREE.MeshBasicMaterial({ map: scorchTex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6 });
      // Instance colour .r is the decal's opacity (lets each scorch fade on its own).
      scorchMat.onBeforeCompile = (sh) => {
        sh.fragmentShader = sh.fragmentShader.replace('#include <color_fragment>', '#if defined( USE_COLOR )\n  diffuseColor.a *= vColor.r;\n#endif');
      };
      const plane = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
      scorch = new THREE.InstancedMesh(plane, scorchMat, SCORCH_MAX);
      for (let i = 0; i < SCORCH_MAX; i++) {
        scorch.setMatrixAt(i, zero);
        scorch.setColorAt(i, col.setRGB(1, 1, 1));
      }
      scorch.frustumCulled = false;
      scorch.name = 'fx-scorch';
      scorch.renderOrder = 1;

      // ripples
      const rippleTex = canvasTexture(64, (g, n) => {
        g.strokeStyle = 'rgba(255,255,255,0.9)';
        g.lineWidth = 3;
        g.beginPath();
        g.arc(n / 2, n / 2, n / 2 - 4, 0, Math.PI * 2);
        g.stroke();
        g.strokeStyle = 'rgba(255,255,255,0.35)';
        g.lineWidth = 2;
        g.beginPath();
        g.arc(n / 2, n / 2, n / 3, 0, Math.PI * 2);
        g.stroke();
      });
      const rippleMat = new THREE.MeshBasicMaterial({ map: rippleTex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
      ripples = new THREE.InstancedMesh(plane, rippleMat, RIPPLE_MAX);
      for (let i = 0; i < RIPPLE_MAX; i++) {
        ripples.setMatrixAt(i, zero);
        ripples.setColorAt(i, col.setRGB(0, 0, 0));
      }
      ripples.frustumCulled = false;
      ripples.name = 'fx-ripples';
      ripples.renderOrder = 4;

      // litter
      const litterGeo = new THREE.PlaneGeometry(0.34, 0.24);
      const litterMat = new THREE.MeshStandardMaterial({ roughness: 0.9, side: THREE.DoubleSide });
      litter = new THREE.InstancedMesh(litterGeo, litterMat, LITTER_MAX);
      const tones: [number, number, number][] = [
        [0.72, 0.7, 0.64],
        [0.55, 0.54, 0.5],
        [0.45, 0.28, 0.12],
        [0.52, 0.36, 0.14],
        [0.3, 0.22, 0.1],
      ];
      for (let i = 0; i < LITTER_MAX; i++) {
        const t = tones[i % tones.length]!;
        litter.setColorAt(i, col.setRGB(t[0], t[1], t[2]));
        litter.setMatrixAt(i, zero);
      }
      litter.frustumCulled = false;
      litter.receiveShadow = true;
      litter.name = 'fx-litter';

      objects.push(scorch, ripples, litter);
      c.scene.add(scorch, ripples, litter);
      disposables.push(scorchTex, scorchMat, plane, rippleTex, rippleMat, litterGeo, litterMat, scorch, ripples, litter);

      c.events.on('vehicleExploded', (ev) => placeScorch(ev.pos.x, ev.pos.z, 5.5));
      c.events.on('vehicleDestroyed', (ev) => placeScorch(ev.pos.x, ev.pos.z, 2.4));
    },
    start() {
      const layout = ctx.services.tryGet('layout');
      if (!layout || !ctx.render) return;
      // pavement sample points (ped paths run along the middle of the pavements)
      const pts: number[] = [];
      for (const path of layout.pedPaths) for (const p of path.points) pts.push(p.x, p.z);
      pavement = new Float32Array(pts);
      for (let j = 0; j < pts.length / 2; j++) {
        const k = cellKey(Math.floor(pts[j * 2]! / CELL), Math.floor(pts[j * 2 + 1]! / CELL));
        const list = paveCells.get(k) ?? [];
        list.push(j);
        paveCells.set(k, list);
      }
      // street vents: a manhole roughly every few blocks on the busier roads (deterministic)
      const vs: number[] = [];
      layout.roads.edges.forEach((ed, i) => {
        if (ed.alley || i % 3 !== 0) return;
        const a = layout.roads.nodes[ed.from]!.pos;
        const b = layout.roads.nodes[ed.to]!.pos;
        const t = 0.35 + (i % 5) * 0.07;
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const l = Math.hypot(dx, dz) || 1;
        const off = ed.laneWidth * 0.5;
        vs.push(a.x + dx * t + (dz / l) * off, a.z + dz * t - (dx / l) * off);
      });
      vents = new Float32Array(vs);
    },
    update(dt) {
      if (!ctx.render || dt <= 0) return;
      const d = Math.min(dt, 0.1);
      const f = focus();

      // ---- scorch fade
      if (scorch) {
        let dirty = false;
        for (let i = 0; i < SCORCH_MAX; i++) {
          if (scorchAge[i]! < 0) continue;
          scorchAge[i] = scorchAge[i]! + d;
          const t = scorchAge[i]! / scorchLife;
          if (t >= 1) {
            scorchAge[i] = -1;
            scorch.setMatrixAt(i, zero);
            scorch.instanceMatrix.needsUpdate = true;
            continue;
          }
          const k = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
          scorch.setColorAt(i, col.setRGB(k, k, k));
          dirty = true;
        }
        if (dirty && scorch.instanceColor) scorch.instanceColor.needsUpdate = true;
      }

      // ---- rain ripples
      if (ripples) {
        const rate = weather === 'rain' ? 260 : weather === 'drizzle' ? 90 : 0;
        rAcc += rate * d;
        while (rAcc >= 1) {
          rAcc -= 1;
          const i = rNext;
          rNext = (rNext + 1) % RIPPLE_MAX;
          rx[i] = f.x + rng.range(-45, 45);
          rz[i] = f.z + 8 + rng.range(-32, 32);
          rAge[i] = 0;
        }
        for (let i = 0; i < RIPPLE_MAX; i++) {
          if (rAge[i]! < 0) continue;
          rAge[i] = rAge[i]! + d;
          const t = rAge[i]! / 0.55;
          if (t >= 1) {
            rAge[i] = -1;
            ripples.setMatrixAt(i, zero);
            continue;
          }
          const sc = 0.12 + t * 0.55;
          m.makeScale(sc, 1, sc).setPosition(rx[i]!, GROUND_Y + 0.01, rz[i]!);
          ripples.setMatrixAt(i, m);
          const a = (1 - t) * 0.32;
          ripples.setColorAt(i, col.setRGB(a * 0.85, a * 0.92, a));
        }
        ripples.instanceMatrix.needsUpdate = true;
        if (ripples.instanceColor) ripples.instanceColor.needsUpdate = true;
      }

      // ---- steam vents (through the shared particle service)
      const particles = ctx.services.tryGet('particles');
      if (particles && vents.length) {
        ventAcc += d * 6;
        if (ventAcc >= 1) {
          ventAcc -= 1;
          for (let i = 0; i < vents.length; i += 2) {
            const x = vents[i]!;
            const z = vents[i + 1]!;
            if (Math.abs(x - f.x) > 60 || Math.abs(z - f.z - 8) > 45) continue;
            particles.emit('engineSmoke', { x, y: 0.15, z }, { count: 1, color: 0xc6ccd2, intensity: 0.55, vel: { x: 0.25, y: 0.9, z: -0.1 } });
          }
        }
      }

      // ---- litter
      if (litter && pavement.length) {
        if (!litterSeeded) {
          for (let i = 0; i < LITTER_MAX; i++) respawnLitter(i, f);
          litterSeeded = true;
        }
        let respawns = 0;
        // The cars that can gust anything: moving, and within the litter field round the focus.
        const vehicles = ctx.services.tryGet('vehicles')?.all();
        gustCount = 0;
        if (vehicles) {
          for (let k = 0; k < vehicles.length && gustCount < GUST_MAX; k++) {
            const veh = vehicles[k]!;
            const vel = veh.velocity;
            const sp2 = vel.x * vel.x + vel.z * vel.z;
            if (sp2 < 49) continue;
            const p = veh.pos;
            if (Math.abs(p.x - f.x) > 76 || Math.abs(p.z - f.z - 8) > 56) continue;
            const g = gustCount * 5;
            gusts[g] = p.x;
            gusts[g + 1] = p.z;
            gusts[g + 2] = vel.x;
            gusts[g + 3] = vel.z;
            gusts[g + 4] = Math.sqrt(sp2);
            gustCount++;
          }
        }
        for (let i = 0; i < LITTER_MAX; i++) {
          const o = i * 3;
          const x = lp[o]!;
          const z = lp[o + 2]!;
          if ((Math.abs(x - f.x) > 70 || Math.abs(z - f.z - 8) > 50) && respawns < 6) {
            respawnLitter(i, f);
            respawns++;
            continue;
          }
          // gusts from passing cars
          if (gustCount && lAir[i] === 0) {
            for (let k = 0; k < gustCount; k++) {
              const g = k * 5;
              const dx = x - gusts[g]!;
              const dz = z - gusts[g + 1]!;
              if (dx * dx + dz * dz > 12) continue;
              const sp = gusts[g + 4]!;
              lAir[i] = 1;
              lv[o] = gusts[g + 2]! * rng.range(0.35, 0.7) + rng.range(-1.5, 1.5);
              lv[o + 1] = rng.range(2.5, 4.5) * Math.min(1.6, sp / 14);
              lv[o + 2] = gusts[g + 3]! * rng.range(0.35, 0.7) + rng.range(-1.5, 1.5);
              lspin[o] = rng.range(-9, 9);
              lspin[o + 1] = rng.range(-6, 6);
              lspin[o + 2] = rng.range(-9, 9);
              break;
            }
          }
          if (lAir[i] === 1) {
            // flutter: strong drag, weak gravity
            const drag = Math.exp(-2.2 * d);
            lv[o] = lv[o]! * drag;
            lv[o + 2] = lv[o + 2]! * drag;
            lv[o + 1] = lv[o + 1]! * Math.exp(-1.2 * d) - 4.2 * d;
            lp[o] = x + lv[o]! * d;
            lp[o + 1] = lp[o + 1]! + lv[o + 1]! * d;
            lp[o + 2] = z + lv[o + 2]! * d;
            lrot[o] = lrot[o]! + lspin[o]! * d;
            lrot[o + 1] = lrot[o + 1]! + lspin[o + 1]! * d;
            lrot[o + 2] = lrot[o + 2]! + lspin[o + 2]! * d;
            if (lp[o + 1]! <= GROUND_Y + 0.01) {
              lp[o + 1] = GROUND_Y + 0.01;
              lAir[i] = 0;
              lrot[o] = -Math.PI / 2 + rng.range(-0.2, 0.2);
              lrot[o + 2] = 0;
            }
            lDirty[i] = 1;
          }
          if (lDirty[i]) {
            lDirty[i] = 0;
            e.set(lrot[o]!, lrot[o + 1]!, lrot[o + 2]!, 'YXZ');
            q.setFromEuler(e);
            m.compose(v.set(lp[o]!, lp[o + 1]!, lp[o + 2]!), q, s.set(1, 1, 1));
            litter.setMatrixAt(i, m);
            litter.instanceMatrix.needsUpdate = true;
          }
        }
      }
    },
    dispose() {
      unsubMood?.();
      for (const o of objects) ctx.scene.remove(o);
      for (const d of disposables) d.dispose();
    },
  };
}
